import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './employeeProfileForm.css';
// import useS3Upload from '../../../../../hooks/aws/useS3Upload';
// import useWasabiUpload from '../../../../../hooks/aws/useWasabiUpload';
import useCsrfToken from '../../../../../hooks/csrf/useCsrfToken';
import useSignedImage from '../../../../../hooks/useSignedImage';
import { userChefIcon } from '../../../../../utils/icons';


const EmployeeProfileForm = ({ onFormSubmit, initialValues, isEditing, setFlashMessage  }) => {

  const [state, setState] = useState({
    name: '',
    surname: '',
    available: '',
    birthDate: '',
    schedule: '',
    period:'',
    country: '',
    genre: '',
    civilState: '',
    profileImageUrl: '',
    phoneNumber: '',
    yearsOfExperience:'',
    region: '',
    comuna: '',
    skills: {
      trabajoEnEquipo: false,
      responsabilidad: false,
      puntualidad: false,
      compromiso: false,
      habilidadParaAprender: false,
      atencionAlCliente: false,
      adaptabilidad: false,
      empatía: false,
      proactividad: false,
      comunicaciónEfectiva: false,
      resoluciónDeProblemas: false,
      organización: false,
      trabajoBajoPresión: false,
      manejoDeSoftware:false
    },
    position: '',
    aboutMe: '',
    experiences: [],
    educations: [],
  });

  const csrfToken = useCsrfToken()

  const MAX_SKILLS = 5;

  const [errors, setErrors] = useState({})


  const regiones = [
    "Arica y Parinacota", "Tarapacá", "Antofagasta", "Atacama", "Coquimbo",
    "Valparaíso", "O'Higgins", "Maule", "Ñuble", "Biobío", "La Araucanía",
    "Los Ríos", "Los Lagos", "Aysén", "Magallanes", "Metropolitana"
  ];

  const comunasSantiago = [
    "Santiago", "Puente Alto", "Maipú", "Las Condes", "La Florida", "Ñuñoa",
    "Vitacura", "Macul", "San Miguel", "Recoleta", "Providencia", "Quilicura",
    "La Pintana", "Pudahuel", "Estación Central", "Pedro Aguirre Cerda"
  ];

  const [uploading, setUploading] = useState(false);


  const IMAGE_CONSTRAINTS = {
    minWidth: 300,
    minHeight: 300,
    maxWidth: 3000,
    maxHeight: 3000,
    minAspectRatio: 0.8, // Allow slightly rectangular (e.g., 300x375px)
    maxAspectRatio: 1.25, // But prefer square for circular display (e.g., 375x300px)
  };

  // Use signed image hook for profile image (only when profileImageUrl exists)
  const { imageUrl: signedProfileImageUrl } = useSignedImage(
    state.profileImageUrl || null, 
    null
  );

  // Helper: Clear image and show error
  const handleImageError = (errorMessage, inputElement, objectUrl) => {
    setErrors((prevErrors) => ({ ...prevErrors, profileImage: errorMessage }));
    setState((prevState) => ({ ...prevState, profileImageUrl: '' }));
    if (inputElement) inputElement.value = '';
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  };

  // Helper: Clear previous image errors
  const clearImageErrors = () => {
    setErrors((prevErrors) => {
      const { profileImage, ...rest } = prevErrors;
      return rest;
    });
  };

  // Helper: Validate image dimensions (warning only, doesn't block submission)
  const validateImageDimensions = (width, height) => {
    const { minWidth, minHeight, maxWidth, maxHeight, minAspectRatio, maxAspectRatio } = IMAGE_CONSTRAINTS;
    const aspectRatio = width / height;

    if (width < minWidth || height < minHeight) {
      return `⚠️ Precaución: La imagen es muy pequeña. Se recomienda dimensiones mínimas: ${minWidth}x${minHeight}px. Tu imagen: ${width}x${height}px. Puedes continuar de todas formas.`;
    }

    if (width > maxWidth || height > maxHeight) {
      return `⚠️ Precaución: La imagen es muy grande. Se recomienda dimensiones máximas: ${maxWidth}x${maxHeight}px. Tu imagen: ${width}x${height}px. Puedes continuar de todas formas.`;
    }

    if (aspectRatio < minAspectRatio || aspectRatio > maxAspectRatio) {
      return `⚠️ Precaución: Se recomienda que la imagen sea aproximadamente cuadrada (relación de aspecto entre ${minAspectRatio} y ${maxAspectRatio}). Tu imagen: ${width}x${height}px. Puedes continuar de todas formas.`;
    }

    return null; // Validation passed
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      handleImageError('Por favor selecciona un archivo de imagen válido.', e.target);
      return;
    }

    // Validate image dimensions (warning only, doesn't block)
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    
    img.onload = () => {
      const validationError = validateImageDimensions(img.width, img.height);
      
      // Always set the preview, even if dimensions don't meet requirements
      setState((prevState) => ({ ...prevState, profileImageUrl: objectUrl }));
      
      // Show warning if dimensions don't meet requirements, but don't block
      if (validationError) {
        setErrors((prevErrors) => ({ ...prevErrors, profileImage: validationError }));
      } else {
        clearImageErrors();
      }
    };

    img.onerror = () => {
      handleImageError('Error al cargar la imagen. Por favor intenta con otro archivo.', e.target, objectUrl);
    };

    img.src = objectUrl;
  };

  useEffect(() => {
    
    if (isEditing && initialValues) {
      
      // Handle skills normalization - only use valid skill keys
      const validSkillKeys = [
        'trabajoEnEquipo', 'responsabilidad', 'puntualidad', 'compromiso',
        'habilidadParaAprender', 'atencionAlCliente', 'adaptabilidad',
        'empatía', 'proactividad', 'comunicaciónEfectiva', 'resoluciónDeProblemas',
        'organización', 'trabajoBajoPresión', 'manejoDeSoftware'
      ];
      let normalizedSkills = {};
      if (initialValues.skills && Array.isArray(initialValues.skills)) {
        normalizedSkills = initialValues.skills.reduce((acc, skill) => {
          // Only add if it's a valid skill key (not a number or invalid key)
          if (validSkillKeys.includes(skill)) {
            acc[skill] = true;
          }
          return acc;
        }, {});
      }

      setState((prevState) => ({
        ...prevState,
        name: initialValues.name || '',
        surname: initialValues.surname || '',
        position: initialValues.position || '',
        available: initialValues.available || '',
        birthDate: initialValues.birthDate || '',
        schedule: initialValues.schedule || '',
        period: initialValues.period || '',
        country: initialValues.country || '',
        genre: initialValues.genre || '',
        civilState: initialValues.civilState || '',
        profileImageUrl: initialValues.profileImageUrl || '',
        phoneNumber: initialValues.phoneNumber || '',
        yearsOfExperience: initialValues.yearsOfExperience || '',
        region: initialValues.region || '',
        comuna: initialValues.comuna || '',
        aboutMe: initialValues.aboutMe || '',
        skills: {
          ...prevState.skills,
          ...normalizedSkills,
        },
        educations: initialValues.educations || [],
        experiences: initialValues.experiences || [],
      }));

    } 
    
  }, [initialValues, isEditing]);

 
  const formatSkillLabel = (skill) => {
    return skill
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^(\p{L})(\p{L}*)/u, (_, firstChar, rest) =>
        firstChar.toUpperCase() + rest
      );
  };

  const addExperience = () => {
    setState((prevState) => ({
      ...prevState,
      experiences: [...prevState.experiences, { role: '', companyName: '', startDate: '', endDate: '', description: '' }],
    }));
  };

  const updateExperience = (index, field, value) => {
    const updatedExperiences = [...state.experiences];
    updatedExperiences[index][field] = value;
    setState((prevState) => ({ ...prevState, experiences: updatedExperiences }));
  };

  const deleteExperience = (index) => {
    setState((prevState) => {
      const updatedExperiences = prevState.experiences.filter((_, i) => i !== index);
      return { ...prevState, experiences: updatedExperiences };
    });
  };

  const addEducation = () => {
    setState((prevState) => ({
      ...prevState,
      educations: [...prevState.educations, { study: '', institution: '', startDate: '', endDate: '', description: '' }],
    }));
  };

  const updateEducation = (index, field, value) => {
    const updatedEducation = [...state.educations];
    updatedEducation[index][field] = value;
    setState((prevState) => ({ ...prevState, educations: updatedEducation }));
  };

  const deleteEducation = (index) => {
    setState((prevState) => {
      const updatedEducations = prevState.educations.filter((_, i) => i !== index);
      return { ...prevState, educations: updatedEducations };
    });
  };

  const updateSkills = (e) => {
    const skillName = e.target.name;
    const isChecked = e.target.checked;

    // Count only valid (non-numeric) skills
    const validSkills = Object.keys(state.skills).filter(skill => !/^\d+$/.test(skill));
    const selectedSkillsCount = validSkills.filter(skill => state.skills[skill]).length + (isChecked ? 1 : -1);

    if (isChecked && selectedSkillsCount >= MAX_SKILLS) {

      setErrors((prevErrors) => ({
        ...prevErrors,
        skills: `Solo puedes seleccionar hasta ${MAX_SKILLS} habilidades.`,
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        skills: '',
      }));
    }

    setState((prevState) => ({
      ...prevState,
      skills: {
        ...prevState.skills,
        [skillName]: isChecked,
      },
    }));
  };

  const validate = (state) => {
    const errors = {};

    if (!state.name) errors.name = 'Campo obligatorio';
    if (!state.position) errors.position = 'Campo obligatorio';
    if (!state.region) errors.region = 'Campo obligatorio';
    if (!state.available) errors.available = 'Campo obligatorio';
    if (!state.schedule) errors.schedule = 'Campo obligatorio';
    if (!state.period) errors.period = 'Campo obligatorio';
    if (!state.country) errors.country = 'Campo obligatorio';
    if (!state.genre) errors.genre = 'Campo obligatorio';
    if (!state.civilState) errors.civilState = 'Campo obligatorio';
    if (!state.yearsOfExperience) errors.yearsOfExperience = 'Campo obligatorio';
    if (!state.phoneNumber) errors.phoneNumber = 'Campo obligatorio';
    if (!state.birthDate) errors.birthDate = 'Campo obligatorio';

    return errors;
  };

  const validateEducationFields = (educations) => {
    if (educations.length === 0) {
      return 'Debes agregar al menos una educación.';
    }

    for (let i = 0; i < educations.length; i++) {
      const edu = educations[i];
      if (!edu.study || !edu.institution || !edu.startDate || !edu.endDate || !edu.description) {
        return `Todos los campos son obligatorios en la educación ${i + 1}`;
      }
    }

    return null;
  };

  const validateExperienceFields = (experiences) => {
    if (experiences.length === 0) {
      return 'Debes agregar al menos una experiencia.';
    }

    for (let i = 0; i < experiences.length; i++) {
      const exp = experiences[i];
      const emptyFields = [];

      if (!exp.role) emptyFields.push('cargo');
      if (!exp.companyName) emptyFields.push('empresa');
      if (!exp.startDate) emptyFields.push('fecha de inicio');
      if (!exp.endDate) emptyFields.push('fecha de término');
      if (!exp.description) emptyFields.push('descripción');

      if (emptyFields.length > 0) {
        return `Los siguientes campos son obligatorios en la experiencia ${i + 1}: ${emptyFields.join(', ')}`;
      }
    }

    return null;
  };

  const validateFields = (state) => {
    const fields = [
      { name: 'name', message: 'El nombre es obligatorio' },
      { name: 'surname', message: 'El apellido es obligatorio' },
      { name: 'position', message: 'La posición es obligatoria' },
      { name: 'region', message: 'La región es obligatoria' },
      { name: 'available', message: 'La disponibilidad es obligatoria' },
      { name: 'schedule', message: 'La jornada es obligatoria' },
      { name: 'period', message: 'El periodo es obligatorio' },
      { name: 'country', message: 'El país es obligatorio' },
      { name: 'genre', message: 'El género es obligatorio' },
      { name: 'civilState', message: 'El estado civil es obligatorio' },
      { name: 'yearsOfExperience', message: 'La experiencia es obligatoria' },
      { name: 'phoneNumber', message: 'El teléfono es obligatorio' },
      { name: 'birthDate', message: 'La fecha de nacimiento es obligatoria' },
      // { name: 'profileImageUrl', message: 'La imagen de perfil es obligatoria' },
    ];

    const errors = {};

    fields.forEach((field) => {
      if (!state[field.name]) {
        errors[field.name] = field.message;
      }
    });

    if (state.region === "Metropolitana" && !state.comuna) {
      errors.comuna = 'La comuna es obligatoria';
    }

    return errors;
  };

  const scrollToField = (field) => {
    const fieldElement = document.querySelector(`#${field}`);
    if (fieldElement) {
        fieldElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
        top: 0,
        behavior: 'smooth',
    });
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setState((prevState) => ({
      ...prevState,
      [id]: value
    }));
  };

  const submitButtonText = isEditing ? 'Actualizar' : 'Crear';

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = {
      ...validateFields(state)
    };

    // Note: profileImage errors are now warnings only and don't block submission

    if (Object.values(validationErrors).some((error) => error)) {
      setErrors((prevErrors) => ({ ...prevErrors, ...validationErrors }));
      scrollToTop();
      setFlashMessage('Rellena todos los campos')
      return;
    }

    setUploading(true);

    try {
      const endpoint = `${process.env.REACT_APP_BACKEND_STAGING_URL}/api/employee`;

      // Create FormData for file uploads
      const formData = new FormData();
      
      // Add all form fields to FormData
      Object.keys(state).forEach(key => {
        if (key === 'skills') {
          // Convert skills object to array, filtering out numeric keys and only including selected skills
          const skillsArray = Object.keys(state.skills)
            .filter(skill => !/^\d+$/.test(skill)) // Filter out numeric keys (0, 1, etc.)
            .filter(skill => state.skills[skill]); // Only include selected skills
          formData.append(key, JSON.stringify(skillsArray));
        } else if (key === 'experiences') {
          // Add experiences as JSON
          formData.append(key, JSON.stringify(state.experiences));
        } else if (key === 'educations') {
          // Add educations as JSON
          formData.append(key, JSON.stringify(state.educations));
        } else if (key === 'birthDate' && state[key]) {
          // Convert birthDate to ISO string format for Prisma
          const date = new Date(state[key]);
          formData.append(key, date.toISOString());
        } else {
          formData.append(key, state[key]);
        }
      });

      // Add profile image if it exists
      const profileImageInput = document.querySelector('input[type="file"][accept*="image"]');
      if (profileImageInput && profileImageInput.files && profileImageInput.files[0]) {
        formData.append('profileImage', profileImageInput.files[0]);
      }

      const response = await fetch(endpoint, {
        method: isEditing ? 'PATCH' : 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        onFormSubmit();
      } else {
        const errorData = await response.json();
        console.error('Failed to submit the form', errorData);
        setFlashMessage('Error al enviar el formulario');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setFlashMessage('Error al enviar el formulario');
    } finally {
      setUploading(false);
      scrollToTop();
    }
  };

  return (
    <form onSubmit={handleSubmit} id="profile-form">
      {/* Photo Upload */}
      <div className="logo-input">
        <label>Foto:</label>
        
        {/* Hidden file input */}
        <input
          type="file"
          accept="image/*"
          onChange={handleLogoUpload}
          disabled={uploading}
          style={{ display: 'none' }}
          id="photo-upload"
        />
        
        {/* Custom styled button that looks like native file input */}
        <div className="logo-input__file-input-wrapper" onClick={() => document.getElementById('photo-upload').click()}>
          <button
            type="button"
            disabled={uploading}
            className="logo-input__custom-button"
          >
            Seleccionar archivo
          </button>
          <span className="logo-input__file-text">
            {state.profileImageUrl ? 'Archivo seleccionado' : 'Ningún archivo seleccionado'}
          </span>
        </div>
        
        {uploading && <p className="logo-input__uploading">Subiendo foto...</p>}
        
        {errors.profileImage && (
          <p className="logo-input__error">{errors.profileImage}</p>
        )}
        
        <div className="company-profile-form__logo-visualization-container">
          {state.profileImageUrl ? (
            <img
              src={signedProfileImageUrl}
              alt="Profile Photo"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <div className="company-profile-form__icon-placeholder">
              {userChefIcon}
            </div>
          )}
        </div>
      </div>

      {/* Inputs */}
      <div className="employee-profile-form__inputs-desctription-container">
        <div className="employee-profile-form__profile-inputs-container">

          {/* Name */}
          <div className="employee-profile-form__group">
            <label htmlFor="name">
              Nombre:
            </label>
            <input
              type="text"
              id="name"
              value={state.name}
              onChange={(e) => setState({ ...state, name: e.target.value })}
              className="employee-profile-form__input"
            />
            {errors.name && <span className="employee-profile-form__error-message">{errors.name}</span>}
          </div>

          {/* Surname */}
          <div className="employee-profile-form__group">
            <label htmlFor="name">
              Apellido:
            </label>
            <input
              type="text"
              id="surname"
              value={state.surname}
              onChange={(e) => setState({ ...state, surname: e.target.value })}
              className="employee-profile-form__input"
            />
            {errors.surname && <span className="employee-profile-form__error-message">{errors.surname}</span>}
          </div>


          <div className="employee-profile-form__group">
            <label htmlFor="position">Posición:</label>
            <select
              id="position"
              value={state.position}
              onChange={(e) => setState((prevState) => ({ ...prevState, position: e.target.value }))}
              className="employee-profile-form__input"
            >
              <option value="">Selecciona una posición</option>
              <option value="Garzón">Garzón</option>
              <option value="Runner">Runner</option>
              <option value="Chef">Chef</option>
              <option value="Ayudante de Cocina">Ayudante de Cocina</option>
              <option value="Anfitrión">Anfitrión</option>
              <option value="Delivery">Delivery</option>
              <option value="Cajero">Cajero</option>
              <option value="Copero">Copero</option>
              <option value="Barista">Barista</option>
              <option value="Bartender">Bartender</option>
              <option value="Sommelier">Sommelier</option>
              <option value="Maitre">Maitre</option>
              <option value="Jefe de salón">Jefe de salón</option>
              <option value="Limpieza">Limpieza</option>
              <option value="Chef Ejecutivo">Chef Ejecutivo</option>
              <option value="Sous Chef">Sous Chef</option>
              <option value="Jefe de Cocina">Jefe de Cocina</option>
              <option value="Maestro de Cocina">Maestro de Cocina</option>
              <option value="Maestro Pastelero">Maestro Pastelero</option>
              <option value="Pastelero">Pastelero</option>
              <option value="Panadero">Panadero</option>
              <option value="Repostero">Repostero</option>
              <option value="Charcutero">Charcutero</option>
              <option value="Pizzero">Pizzero</option>
              <option value="Itamae">Itamae</option>
              <option value="Sushiman">Sushiman</option>
              <option value="Ayudante de Sushi">Ayudante de Sushi</option>
              <option value="Parrillero">Parrillero</option>
              <option value="Cocinero Frío">Cocinero Frío</option>
              <option value="Cocinero Caliente">Cocinero Caliente</option>
              <option value="Manipulador de Alimentos">Manipulador de Alimentos</option>
              <option value="Encargado de Producción">Encargado de Producción</option>
              <option value="Operador de Cocina">Operador de Cocina</option>
              <option value="Operador de Planta">Operador de Planta</option>
              <option value="Operador Multifuncional">Operador Multifuncional</option>
              <option value="Encargado de Reservas">Encargado de Reservas</option>
              <option value="Recepcionista de Restaurante">Recepcionista de Restaurante</option>
              <option value="Supervisor de Salón">Supervisor de Salón</option>
              <option value="Personal de Banquetería">Personal de Banquetería</option>
              <option value="Encargado de Bodega">Encargado de Bodega</option>
              <option value="Repositor">Repositor</option>
              <option value="Personal de Mantenimiento">Personal de Mantenimiento</option>
              <option value="Jefe de Local">Jefe de Local</option>
              <option value="Administrador de Local">Administrador de Local</option>
              <option value="Jefe de Sucursales">Jefe de Sucursales</option>
              <option value="Administrador de Restaurante">Administrador de Restaurante</option>
              <option value="Encargado de Compras">Encargado de Compras</option>
              <option value="Control de Calidad">Control de Calidad</option>
              <option value="Catador de Vinos">Catador de Vinos</option>
              <option value="Coordinador de Banquetes">Coordinador de Banquetes</option>
              <option value="Montajista">Montajista</option>
              <option value="Mixólogo">Mixólogo</option>
            </select>
            {errors.position && <span className="employee-profile-form__error-message">{errors.position}</span>}
          </div>

          <div className="employee-profile-form__group">
            <label htmlFor="region">Región:</label>
            <select
              id="region"
              value={state.region}
              onChange={handleChange}
              className="employee-profile-form__input"
            >
              <option value="">Selecciona una región</option>
              {regiones.map((region, index) => (
                <option key={index} value={region}>{region}</option>
              ))}
            </select>
            {errors.region && <span className="employee-profile-form__error-message">{errors.region}</span>}
          </div>

          <div className="employee-profile-form__group">
            <label htmlFor="comuna">Comuna:</label>
            <select
              id="comuna"
              value={state.comuna}
              onChange={handleChange}
              disabled={state.region !== "Metropolitana"}
              className="employee-profile-form__input"
            >
              <option value="">Selecciona una comuna</option>
              {state.region === "Metropolitana" &&
                comunasSantiago.map((comuna, index) => (
                  <option key={index} value={comuna}>{comuna}</option>
                ))}
            </select>

            {errors.comuna && <span className="employee-profile-form__error-message">{errors.comuna}</span>}
          </div>

          <div className="employee-profile-form__group">
            <label htmlFor="available">
              Disponible:
            </label>
            <select
              id="available"
              value={state.available}
              onChange={(e) => setState({ ...state, available: e.target.value })}
              className="employee-profile-form__input"
            >
              <option value="">Selecciona una disponibilidad</option>
              <option value="Inmediata">Inmediata</option>
              <option value="Programada">Programada</option>
            </select>


            {errors.available && <span className="employee-profile-form__error-message">{errors.available}</span>}
          </div>

          <div className="employee-profile-form__group">
            <label htmlFor="schedule">
              Jornada:
            </label>
            <select
              id="schedule"
              value={state.schedule}
              onChange={(e) => setState({ ...state, schedule: e.target.value })}
              className="employee-profile-form__input"
            >
              <option value="">Selecciona una jornada</option>
              <option value="Tiempo parcial">Tiempo parcial</option>
              <option value="Tiempo completo">Tiempo completo</option>
            </select>
            {errors.schedule && <span className="employee-profile-form__error-message">{errors.schedule}</span>}
          </div>

          <div className="employee-profile-form__group">
            <label htmlFor="period">
              Periodo:
            </label>
            <select
              id="period"
              value={state.period}
              onChange={(e) => setState({ ...state, period: e.target.value })}
              className="employee-profile-form__input"
            >
              <option value="">Selecciona un periodo</option>
              <option value="Permanente">Permanente</option>
              <option value="Reemplazo temporal">Reemplazo temporal</option>
              <option value="Reemplazo urgente">Reemplazo urgente</option>
              <option value="No mencionado">No mencionado</option>
            </select>
            {errors.period && <span className="employee-profile-form__error-message">{errors.period}</span>}
          </div>

          <div className="employee-profile-form__group">
            <label htmlFor="country">
              Pais:
            </label>
            <input
              type="text"
              id="country"
              value={state.country}
              onChange={(e) => setState({ ...state, country: e.target.value })}
              className="employee-profile-form__input"
            />
            {errors.country && <span className="employee-profile-form__error-message">{errors.country}</span>}
          </div>

          <div className="employee-profile-form__group">
            <label htmlFor="genre">Género:</label>
            <select
              id="genre"
              value={state.genre}
              onChange={(e) => setState({ ...state, genre: e.target.value })}
              className="employee-profile-form__input"
            >
              <option value="">Selecciona una género</option>
              <option value="Masculino">Masculino</option>
              <option value="Femenino">Femenino</option>
              <option value="not declared">No declarado</option>
            </select>
            {errors.genre && <span className="employee-profile-form__error-message">{errors.genre}</span>}
          </div>

          <div className="employee-profile-form__group">
            <label htmlFor="civilState">Estado Civil:</label>
            <select
              id="civilState"
              value={state.civilState}
              onChange={(e) => setState({ ...state, civilState: e.target.value })}
              className="employee-profile-form__input"
            >
              <option value="">Selecciona una opción</option>
              <option value="Soltero">Soltero</option>
              <option value="Casado">Casado</option>
              <option value="Divorciado">Divorciado</option>
              <option value="Viudo">Viudo</option>
              <option value="single">Soltero</option>
            </select>
            {errors.civilState && <span className="employee-profile-form__error-message">{errors.civilState}</span>}
          </div>

          <div className="employee-profile-form__group">
            <label htmlFor="yearsOfExperience">Experiencia:</label>
            <select
              id="yearsOfExperience"
              value={state.yearsOfExperience}
              onChange={(e) => setState({ ...state, yearsOfExperience: e.target.value })}
              className="employee-profile-form__input"
            >
              <option value="">Selecciona una opción</option>
              <option value="0 año">Sin experiencia</option>
              <option value="1 año">1 año</option>
              <option value="2 años">2 años</option>
              <option value="3 años">3 años</option>
              <option value="4 años">4 años</option>
              <option value="Más de 5 años">Más de 5 años</option>
              <option value="No experience">Sin experiencia</option>
            </select>
            {errors.yearsOfExperience && <span className="employee-profile-form__error-message">{errors.yearsOfExperience}</span>}
          </div>

          <div className="employee-profile-form__group">
            <label htmlFor="phoneNumber">
              Teléfono:
            </label>
            <input
              type="text"
              id="phoneNumber"
              value={state.phoneNumber}
              onChange={(e) => setState({ ...state, phoneNumber: e.target.value })}
              className="employee-profile-form__input"
            />
            {errors.phoneNumber && <span className="employee-profile-form__error-message">{errors.phoneNumber}</span>}
          </div>

          <div className="employee-profile-form__group">
            <label htmlFor="birthDate">Fecha de nacimiento:</label>
            <DatePicker
              id="birthDate"
              selected={state.birthDate}
              onChange={(date) => setState({ ...state, birthDate: date })}
              className="employee-profile-form__input"
              dateFormat="dd/MM/yyyy"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
            />
          </div>
        </div>

        <div className="employee-profile-form__description-container">
          <label>
            Sobre mi:
          </label>
          <textarea
              id="description-input"
              value={state.aboutMe}
              onChange={(e) => setState((prevState) => ({ ...prevState, aboutMe: e.target.value }))}
              className="employee-profile-form__input"

            />
            {errors.aboutMe && <span className="employee-profile-form__error-message">{errors.aboutMe}</span>}
        </div>
      </div>

      {/* Skills */}
      <div className="employee-profile-form__checkbox-container">
        <h2>Habilidades</h2>
        <div className="employee-profile-form__checkbox-grid ">
          {Object.keys(state.skills)
            .filter(skill => !/^\d+$/.test(skill)) // Filter out numeric keys (0, 1, etc.)
            .map((skill, index) => (
            <div key={skill} className="employee-profile-form__skill-item">
              <input
                id={`skill-check-box-${index}`}
                name={skill}
                type="checkbox"
                checked={state.skills[skill]}
                disabled={!state.skills[skill] && Object.values(state.skills).filter(Boolean).length >= MAX_SKILLS}
                onChange={updateSkills}
              />
              <label htmlFor={`skill-check-box-${index}`}>
                {formatSkillLabel(skill)}
              </label>
            </div>
          ))}
          {errors.skills && <p className="employee-profile-form__error-message">{errors.skills}</p>}
        </div>
      </div>

      {/* Experiencia */}
      <div id="experience" className="employee-profile-form__add-items-container">
        <h2>Experiencia</h2>
        <button type="button" className="employee-profile-form__add-button" onClick={addExperience}>
          + Agregar
        </button>
      </div>
      {errors.experience && (
        <p className="employee-profile-form__error-message">{errors.experience}</p>
      )}
      <div className="employee-profile-form__experiences-container">
        {state.experiences.map((experience, index) => (
          <div key={index}>
            <h3>Experiencia {index + 1}</h3>
            <button
              type="button"
              className="employee-profile-form__delete-button"
              onClick={() => deleteExperience(index)}
            >
              ✕
            </button>
            <div className="employee-profile-form__add-education-form-container">
              <div className="employee-profile-form__inputs-container">
                <div className="employee-profile-form__group">
                  <label>
                    Cargo:
                  </label>
                  <input
                      type="text"
                      value={experience.role}
                      onChange={(e) => updateExperience(index, 'role', e.target.value)}
                      className="employee-profile-form__input"
                  />
                </div>

                <div className="employee-profile-form__group">
                  <label>
                    Empresa:
                  </label>
                  <input
                      type="text"
                      value={experience.companyName}
                      onChange={(e) => updateExperience(index, 'companyName', e.target.value)}
                      className="employee-profile-form__input"
                  />
                </div>
              </div>

              <div className="employee-profile-form__dates-input-container">
                <div className="employee-profile-form__group">
                  <label htmlFor={`start-date-${index}`}>
                    Desde:
                  </label>
                  <DatePicker
                      id={`start-date-${index}`}
                      className="employee-profile-form__start-date-input"
                      selected={experience.startDate}
                      onChange={(date) => updateExperience(index, 'startDate', date)}
                      dateFormat="dd/MM/yyyy"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                  />
                </div>

                <div className="employee-profile-form__group">
                  <label htmlFor={`end-date-${index}`}>
                    Hasta:
                  </label>
                  <DatePicker
                      id={`end-date-${index}`}
                      selected={experience.endDate}
                      onChange={(date) => updateExperience(index, 'endDate', date)}
                      className="employee-profile-form__start-date-input"
                      dateFormat="dd/MM/yyyy"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                  />
                </div>
              </div>

              <div className="employee-profile-form__description-group">
                <label>
                  Descripción:
                </label>
                <textarea
                  value={experience.description}
                  onChange={(e) => updateExperience(index, 'description', e.target.value)}
                  className="employee-profile-form__input"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Educación */}
      <div  className="employee-profile-form__add-items-container">
        <h2>Educación</h2>
        <button type="button" className="employee-profile-form__add-button" onClick={addEducation}>
          + Agregar
        </button>
      </div>
      <div className="employee-profile-form__education-container">
        {state.educations.map((edu, index) => (
          <div key={index}>
            <h3>Educación {index + 1}</h3>
            <button
              type="button"
              className="employee-profile-form__delete-button"
              onClick={() => deleteEducation(index)}
            >
              ✕
            </button>
            <div className="employee-profile-form__add-education-form-container">
              <div className="employee-profile-form__inputs-container">
                <div className="employee-profile-form__group">
                  <label>
                    Estudios:
                  </label>
                  <input
                      type="text"
                      value={edu.study}
                      onChange={(e) => updateEducation(index, 'study', e.target.value)}
                      className="employee-profile-form__input"
                  />
                </div>

                <div className="employee-profile-form__group">
                  <label>
                    Institución:
                  </label>
                  <input
                      type="text"
                      value={edu.institution}
                      onChange={(e) => updateEducation(index, 'institution', e.target.value)}
                      className="employee-profile-form__input"
                    />
                </div>
              </div>

              <div  className="employee-profile-form__dates-input-container">
                <div className="employee-profile-form__group">
                  <label htmlFor={`start-date-${index}`}>
                    Desde:
                  </label>
                  <DatePicker
                    id={`start-date-${index}`}
                    className="employee-profile-form__start-date-input"
                    selected={edu.startDate}
                    onChange={(date) => updateEducation(index, 'startDate', date)}
                    dateFormat="dd/MM/yyyy"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                  />
                </div>
                <div className="employee-profile-form__group">
                  <label htmlFor={`end-date-${index}`}>
                    Hasta:
                  </label>
                  <DatePicker
                    id={`end-date-${index}`}
                    selected={edu.endDate}
                    onChange={(date) => updateEducation(index, 'endDate', date)}
                    className="employee-profile-form__start-date-input"
                    dateFormat="dd/MM/yyyy"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                  />
                </div>
              </div>

              <div className="employee-profile-form__description-group">
                <label>
                  Descripción:
                </label>
                <textarea
                    value={edu.description}
                    onChange={(e) => updateEducation(index, 'description', e.target.value)}
                    className="employee-profile-form__input"
                  />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="employee-profile-form__button-container">
        <button type="submit" className="employee-profile-form__submit-button">{submitButtonText}</button>
      </div>
    </form>
  );
};

export default EmployeeProfileForm;